import { t } from "@travily/i18n";
import { Icon } from "@ui/components/Icon";
import { PopoverSurface } from "@ui/components/PopoverSurface";
import { accentScope } from "@ui/lib/accent-scope";
import { Button } from "@ui/primitives/Button";
import { DialogTrigger } from "@ui/primitives/Popover";
import type { NotificationAction } from "./NotificationItem";
import { type Notification, NotificationPanel } from "./NotificationPanel";

export interface NotificationBellProps {
  hasUnseen: boolean;
  notifications: Notification[];
  onAction: (action: NotificationAction) => void;
  onClose?: () => void;
  onOpen: () => void;
}

export function NotificationBell({ hasUnseen, notifications, onAction, onClose, onOpen }: NotificationBellProps) {
  const handleOpenChange = (isOpen: boolean) => {
    if (isOpen) {
      onOpen();
    } else {
      onClose?.();
    }
  };

  return (
    <DialogTrigger onOpenChange={handleOpenChange}>
      <Button
        {...accentScope("yellow")}
        aria-label={t("components.views.shared.notification_bell.label")}
        className="inline-flex size-11 shrink-0 cursor-pointer items-center justify-center rounded-full outline-none transition-colors hover:bg-primary-hover pressed:bg-primary focus-visible:shadow-focus-ring"
      >
        <span className="relative size-8">
          <Icon name="bell" size="32" type="sticker" />
          {hasUnseen && (
            <span
              aria-hidden="true"
              className="absolute top-[7px] left-5 size-2.5 rounded-full bg-red-400 shadow-[inset_0_-1px_0_var(--color-red-600)] drop-shadow-[0_1px_1px_rgba(0,0,0,0.13)]"
            />
          )}
        </span>
      </Button>
      <PopoverSurface className="w-[400px] overflow-hidden" placement="bottom end">
        <NotificationPanel notifications={notifications} onAction={onAction} />
      </PopoverSurface>
    </DialogTrigger>
  );
}
